import { useEffect, useState } from 'react'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import { Button } from '../Common'
import { requestPreviewGraduation } from '../../hooks/usePreviewGuard'
import { readBlockedActionCounts } from '../../hooks/usePreviewGuard'

const EXIT_INTENT_SEEN_KEY = 'timelining-preview-exit-intent-seen'
const PREVIEW_OPEN_COUNT_KEY = 'timelining-preview-open-count'
const EXIT_INTENT_MIN_OPENS = 3
const EXIT_INTENT_MIN_BLOCKED = 2

/** Reads how many preview workspaces this user has opened this session. */
const readOpenCount = (): number => {
  try {
    const raw = window.sessionStorage.getItem(PREVIEW_OPEN_COUNT_KEY)
    const value = raw ? Number(raw) : 0
    return Number.isFinite(value) ? value : 0
  } catch {
    return 0
  }
}

/** Increment the preview-open counter. Fired from openPreviewWorkspace paths. */
export const trackPreviewOpen = () => {
  try {
    const next = readOpenCount() + 1
    window.sessionStorage.setItem(PREVIEW_OPEN_COUNT_KEY, String(next))
    window.dispatchEvent(new CustomEvent('timelining:preview-opened', { detail: { totalOpens: next } }))
  } catch {
    // ignore storage failures
  }
}

export const PreviewExitIntent = () => {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const checkTrigger = () => {
      try {
        if (window.localStorage.getItem(EXIT_INTENT_SEEN_KEY) === '1') return
      } catch { /* ignore */ }
      const opens = readOpenCount()
      const totalBlocked = Object.values(readBlockedActionCounts()).reduce((sum, n) => sum + n, 0)
      if (opens >= EXIT_INTENT_MIN_OPENS || totalBlocked >= EXIT_INTENT_MIN_BLOCKED) {
        setIsVisible(true)
      }
    }
    const onOpened = () => checkTrigger()
    const onBlocked = () => checkTrigger()
    window.addEventListener('timelining:preview-opened', onOpened)
    window.addEventListener('timelining:preview-blocked', onBlocked)
    checkTrigger()
    return () => {
      window.removeEventListener('timelining:preview-opened', onOpened)
      window.removeEventListener('timelining:preview-blocked', onBlocked)
    }
  }, [])

  const dismiss = () => {
    try {
      window.localStorage.setItem(EXIT_INTENT_SEEN_KEY, '1')
    } catch { /* ignore */ }
    setIsVisible(false)
  }

  if (!isVisible) return null

  return (
    <div className="fixed inset-0 z-[95] flex items-end justify-end p-6 pointer-events-none">
      <div
        // eslint-disable-next-line no-restricted-syntax -- non-modal polite exit-intent toast, not an AppDialog
        role="dialog"
        aria-live="polite"
        className="pointer-events-auto max-w-sm rounded-lg border border-[var(--border)] bg-[var(--surface-card)] p-4 shadow-2xl"
      >
        <p className="text-sm font-bold text-[var(--text-strong)]">You've been exploring for a while.</p>
        <p className="mt-1 text-xs text-[var(--text-muted)]">
          Ready to build your own timeline? Your workspace is already set up — jump in and start adding real work.
        </p>
        <div className="mt-3 flex gap-2 justify-end">
          <Button variant="tertiary" size="sm" onClick={dismiss}>Not yet</Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              dismiss()
              requestPreviewGraduation()
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>Switch to my workspace <ArrowForwardRoundedIcon fontSize="inherit" /></span>
          </Button>
        </div>
      </div>
    </div>
  )
}
